import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { roleKeys, skillKeys } from "@/lib/query";
import type { SkillCreateInput, SkillStatus, SkillUpdateInput } from "@/schemas/skills";

export interface Skill {
  id: string;
  name: string;
  description: string | null;
  /** Many-to-many as of the category/theme merge — a skill can carry several. */
  categories: { id: string; name: string }[];
  // The roles that require this skill, as {id, name}, for linking. Empty when none.
  roles: { id: string; name: string }[];
  status: SkillStatus;
  createdAt: string;
  updatedAt: string;
}

interface SkillsListResponse {
  data: Skill[];
  nextCursor: string | null;
  prevCursor: string | null;
  hasNext: boolean;
  hasPrev: boolean;
  total: number;
}

interface PageRequest {
  cursor: string | null;
  direction: "next" | "prev";
}

interface SkillFilters {
  search: string;
  statuses: string[];
  sort?: string;
  dir?: "asc" | "desc";
}

export function useSkills(filters: SkillFilters, pageRequest: PageRequest) {
  return useQuery({
    queryKey: [...skillKeys.list(filters), pageRequest] as const,
    queryFn: async (): Promise<SkillsListResponse> => {
      const params = new URLSearchParams();
      params.set("limit", "50");
      if (pageRequest.cursor) {
        params.set("cursor", pageRequest.cursor);
        params.set("direction", pageRequest.direction);
      }
      if (filters.search) params.set("search", filters.search);
      if (filters.statuses.length) params.set("status", filters.statuses.join(","));
      if (filters.sort) {
        params.set("sort", filters.sort);
        params.set("dir", filters.dir ?? "asc");
      }
      const res = await fetch(`/api/skills?${params}`);
      if (!res.ok) throw new Error("Failed to fetch skills");
      return res.json();
    },
  });
}

export function useSkill(id: string) {
  return useQuery({
    queryKey: skillKeys.detail(id),
    queryFn: async (): Promise<Skill | null> => {
      const res = await fetch(`/api/skills/${id}`);
      if (res.status === 404) return null;
      if (!res.ok) throw new Error("Failed to fetch skill");
      const json = await res.json();
      return json.data;
    },
  });
}

export interface SkillPerson {
  id: string;
  name: string;
  title: string | null;
  /** The role name(s) via which the person has this skill. */
  roles: string[];
}

/** People who have this skill, derived from the roles they hold that require it. */
export function useSkillPeople(skillId: string) {
  return useQuery({
    queryKey: skillKeys.people(skillId),
    queryFn: async (): Promise<SkillPerson[]> => {
      const res = await fetch(`/api/skills/${skillId}/people`);
      if (!res.ok) throw new Error("Failed to fetch people with this skill");
      const json = await res.json();
      return json.data;
    },
  });
}

export function useCreateSkill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SkillCreateInput) => {
      const res = await fetch("/api/skills", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw new Error("Create failed");
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: skillKeys.all });
      toast.success("Skill created");
    },
    onError: () => toast.error("Failed to create skill"),
  });
}

export function useUpdateSkill(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SkillUpdateInput) => {
      const res = await fetch(`/api/skills/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) throw new Error("Update failed");
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: skillKeys.all });
      // Role requirement lists denormalize skillName — refresh roles so a
      // rename doesn't leave a stale skill label on role detail pages.
      qc.invalidateQueries({ queryKey: roleKeys.all });
      toast.success("Skill updated");
    },
    onError: () => toast.error("Failed to update skill"),
  });
}
